import { useEffect } from "react";
import { Control, FieldValues, get, Path, useController, useFormState } from "react-hook-form";
import { PhoneInput } from "../ui/customs/phone-input";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";

const toE164 = (value: string | undefined, country: CountryCode): string => {
  if (!value || value.startsWith("+")) return value ?? "";
  return parsePhoneNumberFromString(value, country)?.number ?? value;
};

interface StyledPhoneInputProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  title: string;
  control: Control<FieldsType>;
  className?: string;
  inputStyle?: any;
  placeholder?: string;
  disabled?: boolean;
  defaultCountry?: CountryCode;
  required?: boolean;
}

const StyledPhoneInput = <FieldsType extends FieldValues>({
  name,
  control,
  className,
  placeholder,
  disabled = false,
  defaultCountry = "FR",
  required = false,
}: StyledPhoneInputProps<FieldsType>) => {
  const { t } = useTranslation("shared");
  const translateMessage = useTranslateMessage();
  const { errors } = useFormState({ control });
  const errorMessage = translateMessage(get(errors, name + ".message") as unknown as string);
  const { field } = useController({ name, control });
  const normalizedValue = toE164(field.value, defaultCountry);
  const placeholderText = placeholder ?? t("phoneInput.placeholder");

  // Phone numbers may be stored in national format (e.g. "0301234567"), but the input and the
  // form validation expect E.164 (e.g. "+49301234567").
  useEffect(() => {
    if (field.value && normalizedValue !== field.value) {
      field.onChange(normalizedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedValue]);

  return (
    <div className={clsx("flex w-full", className)}>
      <div className={`flex w-full flex-col gap-2`}>
        <PhoneInput
          {...field}
          value={normalizedValue}
          placeholder={required ? `${placeholderText} *` : placeholderText}
          aria-required={required || undefined}
          defaultCountry={defaultCountry}
          className="h-full min-h-[56px]"
          disabled={disabled}
        />
        {errorMessage ? <p className="text-xs text-red-500">{errorMessage}</p> : null}
      </div>
    </div>
  );
};

export default StyledPhoneInput;
